import YAML from 'yaml';

import type { KubesoloConfigResponse } from '@api/types.gen';

import Kubesolo from '@/assets/ico/vendor/kubesolo.svg?c';
import { useIdParam } from '@/react/hooks/useIdParam';
import { useEnvironment } from '@/react/portainer/environments/queries';
import { PageHeader } from '@/react/components/PageHeader';
import { isoDateFromTimestamp } from '@/portainer/filters/filters';

import { Widget, WidgetBody, WidgetTitle } from '@@/Widget';
import { CodeEditor } from '@@/CodeEditor';
import { Code } from '@@/Code';
import { TextTip } from '@@/Tip/TextTip';
import { InlineLoader } from '@@/InlineLoader';

import { isKubeSoloEnvironment } from '../isKubeSoloEnvironment';
import { useKubeSoloConfig } from '../queries/useKubeSoloConfig';

import { KubeSoloVersion } from './KubeSoloVersion';

export function KubeSoloView() {
  const environmentId = useIdParam();
  const { data: environment } = useEnvironment(environmentId);
  const isKubeSolo = isKubeSoloEnvironment(environment);
  const configQuery = useKubeSoloConfig(environmentId, { enabled: isKubeSolo });
  const readAt = configQuery.data?.readAt;

  return (
    <>
      <PageHeader
        title="KubeSolo"
        breadcrumbs={[
          { label: 'Environments', link: 'portainer.endpoints' },
          {
            label: environment?.Name || '',
            link: 'portainer.endpoints.endpoint',
            linkParams: { id: environment?.Id },
          },
          'KubeSolo',
        ]}
      />

      <div className="row">
        <div className="col-sm-12">
          <Widget>
            <WidgetTitle icon={Kubesolo} title="Configuration">
              {!!readAt && (
                <span className="text-muted small" data-cy="kubesolo-read-at">
                  From the snapshot of {isoDateFromTimestamp(readAt)}
                </span>
              )}
            </WidgetTitle>
            <WidgetBody>
              {!isKubeSolo ? (
                <TextTip color="blue">
                  This environment is not a KubeSolo environment.
                </TextTip>
              ) : (
                <KubeSoloConfig
                  config={configQuery.data}
                  isLoading={configQuery.isLoading}
                  isError={configQuery.isError}
                />
              )}
            </WidgetBody>
          </Widget>
        </div>
      </div>
    </>
  );
}

function KubeSoloConfig({
  config,
  isLoading,
  isError,
}: {
  config?: KubesoloConfigResponse | null;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) {
    return (
      <InlineLoader>Reading KubeSolo&apos;s configuration...</InlineLoader>
    );
  }

  if (config === null) {
    return (
      <InlineLoader>
        Waiting for the environment&apos;s first snapshot, which carries
        KubeSolo&apos;s configuration...
      </InlineLoader>
    );
  }

  if (isError || !config) {
    return (
      <TextTip color="red">
        Unable to read KubeSolo&apos;s configuration.
      </TextTip>
    );
  }

  if (config.api === 'notConfigured') {
    return <EnableAPIInstructions />;
  }

  if (config.api === 'unreachable') {
    return (
      <TextTip color="orange">
        KubeSolo&apos;s API did not answer, which usually means KubeSolo is
        restarting. Try again in a minute.
      </TextTip>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <KubeSoloVersion version={config.version} />

      {!!config.error && (
        <TextTip color="orange">
          KubeSolo answered with an error: {config.error}
        </TextTip>
      )}

      {!!config.config && (
        <>
          <CodeEditor
            id="kubesolo-config"
            type="yaml"
            readonly
            value={YAML.stringify(config.config)}
            height="400px"
            data-cy="kubesolo-config-editor"
          />

          <TextTip color="blue">
            To change a setting, run{' '}
            <code>sudo kubesoloctl config set &lt;key&gt; &lt;value&gt;</code>{' '}
            on the machine, then restart KubeSolo.
          </TextTip>
        </>
      )}
    </div>
  );
}

function EnableAPIInstructions() {
  return (
    <div className="flex flex-col gap-2" data-cy="kubesolo-enable-api">
      <TextTip color="orange">
        KubeSolo&apos;s API is not enabled on this machine, so Portainer cannot
        read its configuration. To enable it, run on the machine:
      </TextTip>
      <Code showCopyButton>sudo kubesoloctl config set api.enabled true</Code>
      <p className="text-muted small m-0">
        Then restart KubeSolo, for example with:
      </p>
      <Code showCopyButton>sudo systemctl restart kubesolo</Code>
    </div>
  );
}
